import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrgRole } from "./useOrgRole";

interface Entitlements {
  tier: string;
  maxAgents: number;
  maxSkills: number;
  maxMembers: number;
  maxMessagesMonth: number;
  maxEdgeCallsMonth: number;
  currentAgents: number;
  currentSkills: number;
  currentMembers: number;
  isLoading: boolean;
  canCreate: (entity: "agents" | "skills" | "members") => boolean;
  remaining: (entity: "agents" | "skills" | "members") => number;
}

export function useEntitlements(): Entitlements {
  const { orgId, role } = useOrgRole();

  const { data, isLoading } = useQuery({
    queryKey: ["entitlements", orgId],
    queryFn: async () => {
      if (!orgId) return null;

      const { data: org } = await supabase
        .from("organizations")
        .select("plan")
        .eq("id", orgId)
        .single();

      const tier = org?.plan ?? "free";

      const { data: limits } = await supabase
        .from("plan_limits")
        .select("*")
        .eq("tier", tier)
        .maybeSingle();

      const [agentsRes, membersRes] = await Promise.all([
        supabase.from("syra_ai_agents_safe").select("id", { count: "exact", head: true }).eq("org_id", orgId),
        supabase.from("org_members").select("id", { count: "exact", head: true }).eq("org_id", orgId),
      ]);

      return {
        tier,
        maxAgents: limits?.max_agents ?? 1,
        maxSkills: limits?.max_skills ?? 3,
        maxMembers: limits?.max_members ?? 5,
        maxMessagesMonth: limits?.max_messages_month ?? 1000,
        maxEdgeCallsMonth: limits?.max_edge_calls_month ?? 500,
        currentAgents: agentsRes.count ?? 0,
        currentMembers: membersRes.count ?? 0,
        currentSkills: 0,
      };
    },
    enabled: !!orgId,
    staleTime: 60_000,
  });

  const d = data ?? {
    tier: "free", maxAgents: 1, maxSkills: 3, maxMembers: 5,
    maxMessagesMonth: 1000, maxEdgeCallsMonth: 500,
    currentAgents: 0, currentSkills: 0, currentMembers: 0,
  };

  const limitMap = {
    agents: { max: d.maxAgents, current: d.currentAgents },
    skills: { max: d.maxSkills, current: d.currentSkills },
    members: { max: d.maxMembers, current: d.currentMembers },
  };

  const canBypassAgentLimit = role === "admin" || role === "owner" || role === "superadmin";

  return {
    ...d,
    isLoading,
    canCreate: (entity) => {
      if (entity === "agents" && canBypassAgentLimit) return true;
      return limitMap[entity].max < 0 || limitMap[entity].current < limitMap[entity].max;
    },
    remaining: (entity) => {
      if (entity === "agents" && canBypassAgentLimit) return Infinity;
      return limitMap[entity].max < 0 ? Infinity : Math.max(0, limitMap[entity].max - limitMap[entity].current);
    },
  };
}
